/**
 * Server functions for a representative's "Graduate list" page. Both take
 * the list in a POST body and keep nothing of it: the rules live in
 * `~/lib/server/graduate-list`. The organization comes from the URL slug
 * among those the signed-in person represents.
 *
 * Aliases: graduate list upload, match graduates, confirm graduates
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { authMiddleware } from "~/lib/auth-middleware";
import {
  graduateListConfirmInputSchema,
  graduateListInputSchema,
  type GraduateListOutcome,
  type GraduateListPreview,
} from "~/lib/shared/graduate-list";
import { UserFacingError } from "~/lib/shared/user-facing-error";

/** The organization behind a slug when this person represents it. */
async function graduateListCco(
  userId: string,
  slug: string,
): Promise<{ ccoId: string; programs: { id: string; name: string }[] } | null> {
  const { getMyOrganization } =
    await import("~/lib/server/cco-representatives");
  const organization = await getMyOrganization({ userId, slug });
  if (!organization) return null;
  const { listOrganizationPrograms } =
    await import("~/lib/server/graduate-list");
  return {
    ccoId: organization.ccoId,
    programs: await listOrganizationPrograms(organization.ccoId),
  };
}

async function requireGraduateListCco(
  userId: string,
  slug: string,
): Promise<string> {
  const found = await graduateListCco(userId, slug);
  if (!found) {
    throw new UserFacingError(
      "The graduate list isn't available for this organization.",
    );
  }
  return found.ccoId;
}

/** The organization's programs, or null when the page is not open to them. */
export const getGraduateListSetupFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(z.object({ slug: z.string().min(1) }))
  .handler(
    async ({
      data,
      context,
    }): Promise<{ programs: { id: string; name: string }[] } | null> => {
      const found = await graduateListCco(context.user.id, data.slug);
      return found ? { programs: found.programs } : null;
    },
  );

export const graduateListSetupQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ["graduate-list-setup", slug],
    queryFn: () => getGraduateListSetupFn({ data: { slug } }),
  });

export const previewGraduateListFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(graduateListInputSchema)
  .handler(async ({ data, context }): Promise<GraduateListPreview> => {
    const ccoId = await requireGraduateListCco(context.user.id, data.slug);
    const { enforceGraduateListRateLimit, previewGraduateList } =
      await import("~/lib/server/graduate-list");
    await enforceGraduateListRateLimit({
      step: "check",
      userId: context.user.id,
      ccoId,
    });
    return previewGraduateList({
      ccoId,
      viewer: {
        userId: context.user.id,
        clerkUserId: context.user.clerkUserId,
      },
      rows: data.rows,
    });
  });

export const confirmGraduateListFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(graduateListConfirmInputSchema)
  .handler(async ({ data, context }): Promise<GraduateListOutcome> => {
    const ccoId = await requireGraduateListCco(context.user.id, data.slug);
    const { confirmGraduateList, enforceGraduateListRateLimit } =
      await import("~/lib/server/graduate-list");
    await enforceGraduateListRateLimit({
      step: "confirm",
      userId: context.user.id,
      ccoId,
    });
    return confirmGraduateList({
      ccoId,
      viewer: {
        userId: context.user.id,
        clerkUserId: context.user.clerkUserId,
      },
      rows: data.rows,
      confirm: data.confirm,
      suggest: data.suggest,
    });
  });
